import * as React from 'react';

import { cn } from './lib/cn';

export type BannerVariant = 'info' | 'success' | 'warning' | 'danger' | 'trial';

/**
 * A page-level notice: full-width, in the layout flow, optionally dismissable.
 *
 * NOT `InfoBox`. The two answer different questions and both exist on purpose:
 *
 * - **`Banner`** — page-level, interrupts. Something about the whole session or
 *   screen changed and may need an action.
 * - **`InfoBox`** — inline, constrained, explains. A note beside the thing it is
 *   about. No title, no actions, not dismissable.
 *
 * `info` is NEUTRAL-toned, not blue — the Batch-3 sheet says so in capitals
 * (`KEIN brand-blue (mono!)`). `InfoBox`'s own `info` uses the blue family; that
 * the two differ here is deliberate, and is the clearest case for the rule
 * above.
 *
 * The sheet's warning foregrounds have no `warning-*` token behind them, so this uses the `warning-700` the rest of the system
 * uses rather than writing raw colours into the kit.
 */
const VARIANTS: Record<BannerVariant, { wrap: string; accent: string | null; title: string; desc: string }> = {
  info: {
    wrap: 'bg-neutral-50 border-neutral-300',
    accent: null,
    title: 'text-text-primary',
    desc: 'text-text-secondary',
  },
  success: {
    wrap: 'bg-success-50 border-success-100',
    accent: 'bg-success-500',
    title: 'text-success-700',
    desc: 'text-success-600',
  },
  warning: {
    wrap: 'bg-warning-100 border-warning-200',
    accent: 'bg-warning-500',
    title: 'text-warning-700',
    desc: 'text-warning-700',
  },
  danger: {
    wrap: 'bg-danger-50 border-danger-100',
    accent: 'bg-danger-500',
    title: 'text-danger-700',
    desc: 'text-danger-600',
  },
  trial: {
    wrap: 'bg-banner-trial border-banner-trial',
    accent: null,
    title: 'text-banner-trial-fg',
    // The sheet fades the description with rgba(255,255,255,.72); an opacity
    // on the same foreground token says the same thing without a raw colour.
    desc: 'text-banner-trial-fg opacity-70',
  },
};

export interface BannerProps {
  variant: BannerVariant;
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
  className?: string;
}

export function Banner({
  variant,
  title,
  description,
  icon,
  actions,
  onDismiss,
  dismissLabel = 'Dismiss',
  className,
}: BannerProps) {
  const tone = VARIANTS[variant];

  return (
    <div
      // `danger` is the only variant that interrupts rather than informs, so it
      // is the only one that asserts itself — the same split InfoBox applies.
      role={variant === 'danger' ? 'alert' : 'status'}
      className={cn(
        'relative flex items-start gap-3 overflow-hidden rounded-md border px-5 py-4',
        tone.wrap,
        className,
      )}
    >
      {tone.accent !== null && <span className={cn('absolute inset-y-0 left-0 w-[3px]', tone.accent)} aria-hidden />}
      {icon !== undefined && (
        <span className={cn('mt-0.5 h-6 w-6 shrink-0', tone.title)} aria-hidden>
          {icon}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className={cn('text-base font-medium', tone.title)}>{title}</p>
        {description !== undefined && <p className={cn('mt-1 text-base-sm', tone.desc)}>{description}</p>}
      </div>
      {actions !== undefined && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      {onDismiss !== undefined && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className={cn(
            'shrink-0 rounded-sm p-1 transition-colors duration-micro ease-out focus-visible:outline-none focus-visible:shadow-focus',
            tone.title,
          )}
        >
          <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
            <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  );
}
